import { useRef } from "react";
import { useMapEvents } from "react-leaflet";
import type { LeafletMouseEvent } from "leaflet";

/**
 * How far (in screen pixels) the pointer may travel between press and release
 * and still count as a click rather than a pan.
 *
 * Leaflet fires `click` at the END of a pan on several inputs — trackpads and
 * touch especially — so without this guard dragging the map to look around
 * would drop a marker the dispatcher never asked for. 6px is the usual
 * hand-tremor budget: below it a deliberate tap on a trackpad still registers,
 * above it any real pan is excluded. Dropping a pin by accident onto a
 * half-filled order form is worse than needing a second click (TASK-4997).
 */
export const MAP_CLICK_SLOP_PX = 6;

interface Props {
  /** Called only for a genuine click — never at the end of a pan. */
  onPlace: (lat: number, lon: number) => void;
}

/**
 * Turns a real click on the map into a coordinate, ignoring the click Leaflet
 * emits when a pan finishes.
 *
 * Must be rendered INSIDE a react-leaflet `MapContainer`: `useMapEvents` is how
 * the map instance is reached from a child, the same way `RecenterMap` uses
 * `useMap` (the instance is not addressable from the parent's own render).
 */
export function MapClickCapture({ onPlace }: Props) {
  /** Where the pointer went down, in container pixels. */
  const downPointRef = useRef<{ x: number; y: number } | null>(null);
  /** Set by `movestart`: a pan is unambiguous even when no mousedown was seen. */
  const movedRef = useRef(false);

  useMapEvents({
    mousedown(event: LeafletMouseEvent) {
      downPointRef.current = {
        x: event.containerPoint.x,
        y: event.containerPoint.y,
      };
      movedRef.current = false;
    },
    // A pan started between press and release: this gesture is a drag, full
    // stop. Covers touch and kinetic/inertia panning, where the pointer
    // positions alone can end up within the slop.
    movestart() {
      movedRef.current = true;
    },
    click(event: LeafletMouseEvent) {
      const down = downPointRef.current;
      downPointRef.current = null;

      if (movedRef.current) {
        movedRef.current = false;
        return;
      }
      if (down) {
        const dx = event.containerPoint.x - down.x;
        const dy = event.containerPoint.y - down.y;
        if (Math.hypot(dx, dy) > MAP_CLICK_SLOP_PX) return;
      }

      onPlace(event.latlng.lat, event.latlng.lng);
    },
  });

  return null;
}
